import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '@services/api';
import NavbarStandard from '@commons/NavbarStandard';
import FooterStandard from '@commons/FooterStandard';
import Section from 'components/common/Section';
import { Card, Spinner, Row, Col } from 'react-bootstrap';
import AddToCalendarLinks from './AddToCalendarLinks';
import MeetingEvaluation from './MeetingEvaluation'; // Import du composant séparé
import './PublicParticipantPlanning.css';

const PublicParticipantPlanning = () => {
  const { publicIdentifier } = useParams();
  const [loading, setLoading] = useState(true);
  const [participant, setParticipant] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [salon, setSalon] = useState(null);

  useEffect(() => {
    const fetchPlanning = async () => {
      try {
        const response = await api.get(
          `/public/participant/${publicIdentifier}/planning`
        );
        if (response.data.success) {
          setParticipant(response.data.participant);
          setMeetings(response.data.meetings);
          setSalon(response.data.salon);
        } else {
          toast(response.data.message || 'Impossible de charger le planning.');
        }
      } catch (error) {
        console.error('Erreur lors du chargement du planning:', error);
        toast('Une erreur est survenue lors du chargement du planning.');
      } finally {
        setLoading(false);
      }
    };

    fetchPlanning();
  }, [publicIdentifier]);

  // Déterminer si le participant est une entreprise ou un partenaire
  const isCompanyParticipant = participant?.company_id !== undefined;

  const formatParticipantName = (participant, isPlainText = false) => {
    if (!participant) return 'Non spécifié';

    // NOUVEAU : Gérer le cas où le participant est absent
    if (participant.is_absent) {
      const absentText = `${participant.firstname} ${participant.lastname}`;
      if (isPlainText) {
        return `${absentText} (Absent)`;
      }
      return (
        <span style={{ color: '#dc3545', textDecoration: 'line-through' }}>
          {absentText}
        </span>
      );
    }

    if (participant.is_replaced) {
      const replacedText = `${participant.firstname} ${participant.lastname}`;
      const replacerText = `${participant.replacement_firstname || ''} ${
        participant.replacement_lastname || ''
      }`;

      if (isPlainText) {
        return `${replacedText} ${replacerText}`;
      }

      return (
        <>
          <span style={{ textDecoration: 'line-through' }}>{replacedText}</span>{' '}
          <span>{replacerText}</span>
        </>
      );
    }

    return `${participant.firstname} ${participant.lastname}`;
  };

  if (loading) {
    return (
      <>
        <NavbarStandard />
        <div className="mt-5">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: '80vh' }}
          >
            <Spinner animation="border" />
          </div>
        </div>
        <FooterStandard />
      </>
    );
  }

  if (!participant) {
    return (
      <>
        <NavbarStandard />
        <div className="mt-5">
          <div className="text-center mt-5">
            <h5 className="text-danger">Participant introuvable.</h5>
          </div>
        </div>
        <FooterStandard />
      </>
    );
  }

  return (
    <>
      <NavbarStandard />
      <div className="mt-5">
        {/* Carte sticky avec les informations du salon */}
        <Section bg="light" className="sticky-card">
          <Row>
            <Col xs={12}>
              <Card className="mb-4">
                <Card.Body>
                  <h5 className="text-center">
                    <strong>{salon?.name || 'Non spécifié'}</strong>
                  </h5>
                  <p className="text-center mb-0">
                    <strong>Date :</strong>{' '}
                    {salon?.date
                      ? new Date(salon.date).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric'
                        })
                      : 'Non spécifiée'}
                  </p>
                  <p className="text-center">
                    <strong>Heure de démarrage :</strong>{' '}
                    {salon?.start_time || 'Non spécifiée'} |{' '}
                    <strong>Heure de fin :</strong>{' '}
                    {salon?.end_time || 'Non spécifiée'} |{' '}
                    <strong>Heure du déjeuner :</strong>{' '}
                    {salon?.lunch_break_start_time || 'Non spécifiée'}
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Section>

        {/* Liste des rendez-vous */}
        <Section bg="light" className="d-flex align-items-end p-1 mt-8">
          <Row>
            {meetings.length > 0 ? (
              meetings.map((meeting, index) => (
                <Col xs={12} key={index} className="mb-3">
                  <Card>
                    <Card.Body>
                      <Card.Title>
                        {meeting.start_time} - {meeting.end_time}
                      </Card.Title>
                      <Card.Text className="mb-1">
                        <strong
                          style={{
                            fontSize: '1.2rem',
                            display: 'block',
                            marginBottom: '0.5rem'
                          }}
                        >
                          {meeting.associatedEntities?.length > 0
                            ? meeting.associatedEntities.join(', ')
                            : 'Non spécifié'}
                        </strong>
                      </Card.Text>
                      <Card.Text>
                        <strong>Avec :</strong>{' '}
                        {meeting.associatedParticipants?.length > 0
                          ? meeting.associatedParticipants.map((p, index) => (
                              <span key={index}>
                                {formatParticipantName(p)}
                                {index <
                                  meeting.associatedParticipants.length - 1 &&
                                  ', '}
                              </span>
                            ))
                          : 'Non spécifié'}
                      </Card.Text>
                      {/* Utiliser le nouveau composant ici */}
                      <AddToCalendarLinks
                        meeting={meeting}
                        salon={salon}
                        formatParticipantName={formatParticipantName}
                      />
                      <hr />
                      <MeetingEvaluation
                        meetingId={meeting.id}
                        evaluatedId={
                          isCompanyParticipant
                            ? meeting.partner_id
                            : meeting.company_id
                        }
                        evaluatedType={
                          isCompanyParticipant ? 'partner' : 'company'
                        }
                        evaluatorId={participant.id}
                        evaluatorType={
                          isCompanyParticipant ? 'company' : 'partner'
                        }
                        evaluatorUuid={publicIdentifier}
                        initialRating={meeting.evaluation?.rating || null}
                        initialMeetingOccurred={
                          meeting.evaluation?.meeting_occurred ?? null
                        }
                      />
                    </Card.Body>
                  </Card>
                </Col>
              ))
            ) : (
              <Col xs={12} className="text-center">
                <p className="text-muted">
                  Aucun rendez-vous prévu pour cette journée.
                </p>
              </Col>
            )}
          </Row>
        </Section>
      </div>
      <FooterStandard />
    </>
  );
};

export default PublicParticipantPlanning;
